import { Button } from '../../../core/components/Button';
import logo from '../../../assets/logo.png';
import { ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useHomepage } from '../../../hooks/useHomepage';

export const Footer = () => {
    const { t, i18n } = useTranslation('shop');
    const isRTL = i18n.language === 'ar';
    const { data: homepageData } = useHomepage();
    const footer = homepageData?.footer;
    const location = useLocation();

    // Check if we are on a product page to lift the WhatsApp button above the sticky cart bar
    const isProductPage = location.pathname.startsWith('/product/');

    return (
        <footer className="bg-secondary text-white pt-12 lg:pt-20 pb-8 lg:pb-10 relative overflow-hidden">
            <div className="container mx-auto px-4 relative z-10">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12 mb-10 lg:mb-16">

                    {/* Logo & Description */}
                    <div className="col-span-1 sm:col-span-2 lg:col-span-1">
                        <img src={logo} alt="El Raay Optics" className="h-12 lg:h-16 mb-4 lg:mb-6 filter brightness-0 invert opacity-90" />
                        <p className="text-gray-400 text-sm lg:text-base leading-relaxed font-light max-w-xs">
                            {footer?.description || "Trust of Egypt's favorite optics since 2005. bringing clarity and style to your vision."}
                        </p>
                    </div>

                    {/* Categories */}
                    <div>
                        <h4 className="text-base lg:text-lg font-bold mb-6 text-primary tracking-wide">تسوق حسب الفئة</h4>
                        <ul className="space-y-4 text-sm lg:text-base text-gray-300">
                            {homepageData?.categories?.length ? (
                                homepageData.categories.slice(0, 5).map((category: any) => (
                                    <li key={category.id}>
                                        <a href={`/category/${category.slug || category.id}`} className="flex items-center gap-2 group hover:text-white transition-colors duration-300">
                                            <span className="w-0 overflow-hidden group-hover:w-4 transition-all duration-300 text-primary">
                                                <ArrowRight size={14} />
                                            </span>
                                            <span className="group-hover:translate-x-1 transition-transform duration-300">{category.name || category.nameAr}</span>
                                        </a>
                                    </li>
                                ))
                            ) : (
                                [t('footer.trackOrder'), t('footer.returnPolicy'), t('footer.storeLocator'), t('footer.bookEyeExam')].map((link) => (
                                    <li key={link}>
                                        <a href="#" className="flex items-center gap-2 group hover:text-white transition-colors duration-300">
                                            <span className="w-0 overflow-hidden group-hover:w-4 transition-all duration-300 text-primary">
                                                <ArrowRight size={14} />
                                            </span>
                                            <span className="group-hover:translate-x-1 transition-transform duration-300">{link}</span>
                                        </a>
                                    </li>
                                ))
                            )}
                        </ul>
                    </div>

                    {/* Brands */}
                    <div>
                        <h4 className="text-base lg:text-lg font-bold mb-6 text-primary tracking-wide">أشهر الماركات</h4>
                        <ul className="space-y-4 text-sm lg:text-base text-gray-300">
                            {homepageData?.brands?.length ? (
                                homepageData.brands.slice(0, 5).map((brand: any) => (
                                    <li key={brand.id}>
                                        <a href={`/brand/${brand.slug || brand.id}`} className="flex items-center gap-2 group hover:text-white transition-colors duration-300">
                                            <span className="w-0 overflow-hidden group-hover:w-4 transition-all duration-300 text-primary">
                                                <ArrowRight size={14} />
                                            </span>
                                            <span className="group-hover:translate-x-1 transition-transform duration-300">{brand.name || brand.nameAr}</span>
                                        </a>
                                    </li>
                                ))
                            ) : (
                                [t('footer.faq'), t('footer.contactUs'), t('footer.shippingInfo')].map((link) => (
                                    <li key={link}>
                                        <a href="#" className="flex items-center gap-2 group hover:text-white transition-colors duration-300">
                                            <span className="w-0 overflow-hidden group-hover:w-4 transition-all duration-300 text-primary">
                                                <ArrowRight size={14} />
                                            </span>
                                            <span className="group-hover:translate-x-1 transition-transform duration-300">{link}</span>
                                        </a>
                                    </li>
                                ))
                            )}
                        </ul>
                    </div>

                    {/* Policies & Support */}
                    <div>
                        <h4 className="text-base lg:text-lg font-bold mb-6 text-primary tracking-wide">
                            {isRTL ? 'السياسات والدعم' : 'Policies & Support'}
                        </h4>
                        <ul className="space-y-4 text-sm lg:text-base text-gray-300">
                            <li>
                                <a href="/legal/privacy" className="flex items-center gap-2 group hover:text-white transition-colors duration-300">
                                    <span className="w-0 overflow-hidden group-hover:w-4 transition-all duration-300 text-primary">
                                        <ArrowRight size={14} className={isRTL ? "rotate-180" : ""} />
                                    </span>
                                    <span className="group-hover:translate-x-1 transition-transform duration-300">
                                        {t('footer.privacyPolicy', 'سياسة الخصوصية')}
                                    </span>
                                </a>
                            </li>
                            <li>
                                <a href="/legal/terms" className="flex items-center gap-2 group hover:text-white transition-colors duration-300">
                                    <span className="w-0 overflow-hidden group-hover:w-4 transition-all duration-300 text-primary">
                                        <ArrowRight size={14} className={isRTL ? "rotate-180" : ""} />
                                    </span>
                                    <span className="group-hover:translate-x-1 transition-transform duration-300">
                                        {t('footer.terms', 'الشروط والأحكام')}
                                    </span>
                                </a>
                            </li>
                            <li>
                                <a href="/legal/delivery" className="flex items-center gap-2 group hover:text-white transition-colors duration-300">
                                    <span className="w-0 overflow-hidden group-hover:w-4 transition-all duration-300 text-primary">
                                        <ArrowRight size={14} className={isRTL ? "rotate-180" : ""} />
                                    </span>
                                    <span className="group-hover:translate-x-1 transition-transform duration-300">
                                        {t('footer.shippingInfo', 'سياسة الشحن')}
                                    </span>
                                </a>
                            </li>
                            <li>
                                <a href="/legal/returns" className="flex items-center gap-2 group hover:text-white transition-colors duration-300">
                                    <span className="w-0 overflow-hidden group-hover:w-4 transition-all duration-300 text-primary">
                                        <ArrowRight size={14} className={isRTL ? "rotate-180" : ""} />
                                    </span>
                                    <span className="group-hover:translate-x-1 transition-transform duration-300">
                                        {t('footer.returnPolicy', 'سياسة الإرجاع')}
                                    </span>
                                </a>
                            </li>
                            <li>
                                <a href="/contact" className="flex items-center gap-2 group hover:text-white transition-colors duration-300">
                                    <span className="w-0 overflow-hidden group-hover:w-4 transition-all duration-300 text-primary">
                                        <ArrowRight size={14} className={isRTL ? "rotate-180" : ""} />
                                    </span>
                                    <span className="group-hover:translate-x-1 transition-transform duration-300">
                                        {t('footer.contactUs', 'اتصل بنا')}
                                    </span>
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Copyright */}
                <div className="border-t border-white/10 pt-6 lg:pt-8 flex flex-col sm:flex-row justify-between items-center text-gray-500 text-xs lg:text-sm gap-4">
                    <p>{footer?.copyright || '© 2026 El Raay Optics. All rights reserved.'}</p>
                    <div className="flex gap-4 lg:gap-6">
                        <a href="/legal/privacy" className="hover:text-white transition">{t('footer.privacyPolicy', 'سياسة الخصوصية')}</a>
                        <a href="/legal/terms" className="hover:text-white transition">{t('footer.terms', 'الشروط والأحكام')}</a>
                    </div>
                </div>
            </div>

            {/* Floating WhatsApp Button */}
            {(footer?.whatsappEnabled ?? true) && (
                <a
                    href={footer?.whatsappNumber ? `https://wa.me/${footer.whatsappNumber}` : "https://wa.me/201002895951"}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`fixed z-50 bg-[#25D366] text-white w-[60px] h-[60px] rounded-full shadow-2xl hover:scale-110 transition-transform duration-300 flex items-center justify-center
                        ${isRTL ? 'left-4 lg:left-6' : 'right-4 lg:right-6'}
                        ${isProductPage ? 'bottom-[100px] lg:bottom-6' : 'bottom-4 lg:bottom-6'}
                    `}
                    aria-label="Chat on WhatsApp"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.506-.669-.516-.173-.009-.371-.009-.57-.009-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" />
                    </svg>
                </a>
            )}
        </footer>
    );
};
